import { openDayDate } from '../../utils/openDayDate';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  DatePicker,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
  Row,
  Col,
  message,
} from 'antd';
import {
  PlayCircleOutlined,
  ExperimentOutlined,
  HomeOutlined,
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import i18n, { datePickerFormat, formatDate as formatLocaleDate, formatDateTime } from '../../i18n';
import MainLayout from '../../components/common/MainLayout';
import AppModeCards from '../../components/navigation/AppModeCards';
import {
  useCreateFreeSaveMutation,
  useDeleteSaveMutation,
  useListSavesQuery,
  useListSystemWeatherMapsQuery,
} from '../../api/savesV2Api';
import type { Save, SaveStatus, SaveType } from '../../types/saves';
import type { WeatherMapItem } from '../../types/simulation';
import GuidedSaveCreationWizard from '../../components/saves/GuidedSaveCreationWizard';

const { Title, Text } = Typography;

// Подписи: saves:shared.type.<type> / saves:shared.status.<status>
const SAVE_TYPE_META: Record<SaveType, { color: string; icon: React.ReactNode }> = {
  free: { color: 'green', icon: <HomeOutlined /> },
  scenario: { color: 'orange', icon: <ExperimentOutlined /> },
};

const STATUS_META: Record<string, { color: string }> = {
  active: { color: 'blue' },
  completed: { color: 'green' },
  archived: { color: 'default' },
  requires_migration: { color: 'red' },
};

type TypeFilter = 'all' | SaveType;
type StatusFilter = 'active' | 'all' | SaveStatus;

const formatDate = (iso: string | null | undefined, fallback = '—') =>
  iso ? formatDateTime(iso, 'short') : fallback;

const formatSimDate = (save: Save) => {
  const date = openDayDate(save.cursor?.simDate || save.cursor?.sim_date, save.type);
  return date ? formatLocaleDate(date, 'short') : '—';
};

const defaultSaveName = () => i18n.t('saves:shared.defaultSaveName');
const defaultFieldName = () => i18n.t('saves:list.defaultFieldName');
const defaultPlotName = () => i18n.t('saves:list.defaultPlotName');
const measurementOptions = () => [
  { label: i18n.t('saves:shared.measurement.metric'), value: 'metric' },
  { label: i18n.t('saves:shared.measurement.imperial'), value: 'imperial' },
];

const metadataOf = (map: WeatherMapItem | null | undefined): Record<string, unknown> => (
  (map?.metadata || {}) as Record<string, unknown>
);

const coordsFromWeatherMap = (map: WeatherMapItem | null | undefined): string | undefined => {
  const meta = metadataOf(map);
  const lat = Number(meta.lat);
  const lon = Number(meta.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return undefined;
  return `${lat},${lon}`;
};

const buildWeatherMapOptions = (maps: WeatherMapItem[]) => {
  const groups = new Map<string, { label: string; options: { label: string; value: string }[] }>();
  maps.forEach((map) => {
    const meta = metadataOf(map);
    const groupId = String(meta.regionGroup || 'system');
    const groupLabel = String(meta.regionGroupName || i18n.t('saves:list.presetMaps'));
    if (!groups.has(groupId)) {
      groups.set(groupId, { label: groupLabel, options: [] });
    }
    const country = meta.countryName ? `, ${meta.countryName}` : '';
    const koppen = meta.koppen ? ` · ${meta.koppen}` : '';
    const regionName = meta.regionName ? String(meta.regionName) : map.name;
    const variant = meta.yearTypeName ? ` · ${meta.yearTypeName}` : '';
    groups.get(groupId)?.options.push({
      value: map.id,
      label: `${regionName}${variant}${country}${koppen}`,
    });
  });
  return Array.from(groups.values());
};

const SaveCard: React.FC<{
  save: Save;
  deleting: boolean;
  onDelete: (save: Save) => void;
}> = ({ save, deleting, onDelete }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('saves');
  const typeMeta = SAVE_TYPE_META[save.type]
    ? { ...SAVE_TYPE_META[save.type], label: t(`shared.type.${save.type}`) }
    : { label: save.type, color: 'default', icon: null };
  const statusMeta = STATUS_META[save.status]
    ? { ...STATUS_META[save.status], label: t(`shared.status.${save.status}`) }
    : { label: save.status, color: 'default' };
  const tier = save.capabilitiesSnapshot?.tier;
  const canDelete = save.status !== 'archived';

  return (
    <Card
      hoverable
      onClick={() => navigate(`/saves/${save.id}`)}
      style={{ height: '100%' }}
      title={
        <Space>
          <span>{typeMeta.icon}</span>
          <Tag color={typeMeta.color}>{typeMeta.label}</Tag>
          <Tag color={statusMeta.color}>{statusMeta.label}</Tag>
          {tier ? <Tag color="purple">{`Tier: ${tier}`}</Tag> : null}
        </Space>
      }
      extra={
        <Space size={4} onClick={(e) => e.stopPropagation()}>
          <Button
            size="small"
            type="link"
            onClick={() => navigate(`/saves/${save.id}`)}
          >
            {t('common:actions.open')}
          </Button>
          {canDelete ? (
            <Popconfirm
              title={t('list.deleteConfirm.title')}
              description={t('list.deleteConfirm.description')}
              okText={t('common:actions.delete')}
              okType="danger"
              cancelText={t('common:actions.cancel')}
              onConfirm={() => onDelete(save)}
            >
              <Button
                size="small"
                type="link"
                danger
                icon={<DeleteOutlined />}
                loading={deleting}
              >
                {t('common:actions.delete')}
              </Button>
            </Popconfirm>
          ) : null}
        </Space>
      }
    >
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <div>
          <Text type="secondary">ID: </Text>
          <Text code copyable={{ text: save.id }}>
            {save.id.slice(0, 8)}…
          </Text>
        </div>
        <div>
          <Text type="secondary">{t('list.card.simDate')} </Text>
          <Text strong>{formatSimDate(save)}</Text>
        </div>
        {save.measurementSystem ? (
          <div>
            <Text type="secondary">{t('list.card.scale')} </Text>
            <Text>
              {save.measurementSystem === 'imperial' ? t('shared.measurement.imperial') : t('shared.measurement.metric')}
              {save.planScale?.display?.cellSize ? ` · ${save.planScale.display.cellSize}` : ''}
            </Text>
          </div>
        ) : null}
        {save.type === 'free' && save.farmId ? (
          <div>
            <Text type="secondary">{t('shared.containerFarm')}: </Text>
            <Text>{save.farmName ? `${save.farmName} ` : ''}</Text>
            <Text code>{save.farmId.slice(0, 8)}…</Text>
          </div>
        ) : null}
        {save.type === 'scenario' && save.scenarioTemplateId ? (
          <div>
            <Text type="secondary">{t('shared.type.scenario')}: </Text>
            <Text code>{save.scenarioTemplateId.slice(0, 8)}…</Text>
            {save.scenarioTemplateVersion != null ? (
              <Text type="secondary"> v{save.scenarioTemplateVersion}</Text>
            ) : null}
          </div>
        ) : null}
        <div>
          <Text type="secondary">Cache policy: </Text>
          <Text>{save.cachePolicy}</Text>
        </div>
        <div>
          <Text type="secondary">Engine: </Text>
          <Text>{save.engineVersion}</Text>
          <Text type="secondary"> · revision </Text>
          <Text>{String(save.revision)}</Text>
        </div>
        <div>
          <Text type="secondary">{t('shared.lastPlayed')}: </Text>
          <Text>{formatDate(save.lastPlayedAt, t('shared.neverPlayed'))}</Text>
        </div>
        <div>
          <Text type="secondary">{t('shared.createdAt')}: </Text>
          <Text>{formatDate(save.createdAt)}</Text>
        </div>
      </Space>
    </Card>
  );
};

export default function SavesListPage() {
  const { t } = useTranslation('saves');
  const navigate = useNavigate();
  const location = useLocation();
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [form] = Form.useForm();
  const selectedWeatherSeriesId = Form.useWatch('weatherSeriesId', form);
  const {
    data: weatherMaps = [],
    isError: weatherMapsError,
    isFetching: weatherMapsFetching,
    isLoading: weatherMapsLoading,
  } = useListSystemWeatherMapsQuery(undefined, {
    skip: !createModalOpen,
  });
  const [createFreeSave, { isLoading: creatingFreeSave }] = useCreateFreeSaveMutation();
  const [deleteSave] = useDeleteSaveMutation();
  const [deletingSaveId, setDeletingSaveId] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const type = params.get('type');
    const status = params.get('status');
    if (type === 'free' || type === 'scenario') setTypeFilter(type);
    if (status === 'active' || status === 'completed' || status === 'archived' || status === 'requires_migration' || status === 'all') {
      setStatusFilter(status as StatusFilter);
    }
  }, [location.search]);

  const queryArgs = useMemo(() => {
    const args: { type?: SaveType; status?: SaveStatus; limit?: number } = { limit: 100 };
    if (typeFilter !== 'all') args.type = typeFilter;
    if (statusFilter !== 'all') args.status = statusFilter as SaveStatus;
    return args;
  }, [typeFilter, statusFilter]);

  const { data: saves, isLoading, isError, refetch } = useListSavesQuery(queryArgs);

  const selectedWeatherMap = useMemo(
    () => weatherMaps.find((map) => map.id === selectedWeatherSeriesId) || null,
    [weatherMaps, selectedWeatherSeriesId],
  );

  const weatherMapOptions = useMemo(() => buildWeatherMapOptions(weatherMaps), [weatherMaps]);

  useEffect(() => {
    if (!createModalOpen || !weatherMaps[0]) return;
    const current = form.getFieldValue('weatherSeriesId');
    if (!current) {
      form.setFieldsValue({
        weatherSeriesId: weatherMaps[0].id,
        location_coords: coordsFromWeatherMap(weatherMaps[0]),
      });
    }
  }, [createModalOpen, form, weatherMaps]);

  const openCreateModal = () => {
    form.setFieldsValue({
      name: defaultSaveName(),
      startDate: dayjs(),
      measurementSystem: 'metric',
      fieldName: defaultFieldName(),
      plotName: defaultPlotName(),
    });
    setCreateModalOpen(true);
  };

  const handleWeatherChange = (weatherSeriesId: string) => {
    const selected = weatherMaps.find((map) => map.id === weatherSeriesId);
    form.setFieldsValue({ location_coords: coordsFromWeatherMap(selected) });
  };

  const handleCreateFreeSave = async () => {
    try {
      const values = await form.validateFields();
      const startDate = values.startDate?.format
        ? values.startDate.format('YYYY-MM-DD')
        : dayjs().format('YYYY-MM-DD');
      const payload: Record<string, unknown> = {
        name: values.name || defaultSaveName(),
        description: values.description || '',
        startDate,
        measurementSystem: values.measurementSystem || 'metric',
        location_coords: values.location_coords || coordsFromWeatherMap(selectedWeatherMap),
        fieldName: defaultFieldName(),
        plotName: defaultPlotName(),
      };
      if (values.weatherSeriesId) {
        payload.weatherSeriesId = values.weatherSeriesId;
      }
      const result = await createFreeSave(payload).unwrap();
      form.resetFields();
      setCreateModalOpen(false);
      const saveId = result?.save?.id;
      if (saveId) {
        navigate(`/saves/${saveId}`);
      } else {
        message.success(t('shared.created'));
        refetch();
      }
    } catch (err: any) {
      if (!err?.errorFields) {
        const e = err as { data?: { message?: string } };
        message.error(e?.data?.message || t('shared.createFailed'));
      }
    }
  };

  const handleDeleteSave = async (save: Save) => {
    setDeletingSaveId(save.id);
    try {
      await deleteSave(save.id).unwrap();
      message.success(t('list.deleted'));
      refetch();
    } catch (err) {
      const e = err as { data?: { message?: string } };
      message.error(e?.data?.message || t('list.deleteFailed'));
    } finally {
      setDeletingSaveId(null);
    }
  };

  return (
    <MainLayout
      breadcrumb={
        <Breadcrumb
          items={[
            { title: <Link to="/">{t('shared.breadcrumbHome')}</Link> },
            { title: t('shared.breadcrumbSaves') },
          ]}
        />
      }
    >
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <Title level={3} style={{ margin: 0 }}>
          <PlayCircleOutlined /> {t('list.title')}
        </Title>
        <Space wrap>
          <Button type="primary" icon={<HomeOutlined />} onClick={() => setWizardOpen(true)}>
            {t('list.createSave')}
          </Button>
          <Button icon={<HomeOutlined />} onClick={openCreateModal}>
            {t('list.quickCreate')}
          </Button>
          <Button
            icon={<PlusOutlined />}
            onClick={() => navigate('/scenarios')}
          >
            {t('list.startScenario')}
          </Button>
        </Space>
      </Space>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('list.title')}
        description={t('list.intro')}
      />

      <div style={{ marginBottom: 16 }}>
        <AppModeCards compact />
      </div>

      <Space wrap style={{ marginBottom: 16 }}>
        <Space>
          <Text type="secondary">{t('list.filters.type')}</Text>
          <Segmented
            value={typeFilter}
            onChange={(v) => setTypeFilter(v as TypeFilter)}
            options={[
              { label: t('common:states.all'), value: 'all' },
              { label: t('list.filters.free'), value: 'free' },
              { label: t('list.filters.scenarios'), value: 'scenario' },
            ]}
          />
        </Space>
        <Space>
          <Text type="secondary">{t('list.filters.status')}</Text>
          <Segmented
            value={statusFilter}
            onChange={(v) => setStatusFilter(v as StatusFilter)}
            options={[
              { label: t('list.filters.active'), value: 'active' },
              { label: t('list.filters.completed'), value: 'completed' },
              { label: t('list.filters.archived'), value: 'archived' },
              { label: t('common:states.all'), value: 'all' },
            ]}
          />
        </Space>
        <Button onClick={() => refetch()}>{t('common:actions.refresh')}</Button>
      </Space>

      {isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('list.loadFailed')}
        />
      )}

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 50 }}>
          <Spin size="large" />
        </div>
      ) : !saves || saves.length === 0 ? (
        <Empty description={t('list.empty')} image={Empty.PRESENTED_IMAGE_SIMPLE}>
          <Space>
            <Button type="primary" onClick={() => setWizardOpen(true)}>{t('list.createSave')}</Button>
            <Button onClick={() => navigate('/scenarios')}>{t('list.openScenarioCatalog')}</Button>
          </Space>
        </Empty>
      ) : (
        <Row gutter={[16, 16]}>
          {saves.map((save) => (
            <Col key={save.id} xs={24} md={12} xl={8}>
              <SaveCard
                save={save}
                deleting={deletingSaveId === save.id}
                onDelete={handleDeleteSave}
              />
            </Col>
          ))}
        </Row>
      )}

      <Modal
        title={t('list.quickCreateTitle')}
        open={createModalOpen}
        onOk={handleCreateFreeSave}
        onCancel={() => setCreateModalOpen(false)}
        okText={t('common:actions.create')}
        cancelText={t('common:actions.cancel')}
        confirmLoading={creatingFreeSave}
      >
        <Form form={form} layout="vertical">
          <Form.Item label={t('shared.saveName')} name="name" rules={[{ required: true, message: t('list.nameRequired') }]}>
            <Input />
          </Form.Item>
          <Form.Item
            label={t('list.weatherMap')}
            name="weatherSeriesId"
          >
            <Select
              allowClear
              showSearch
              loading={weatherMapsLoading || weatherMapsFetching}
              options={weatherMapOptions}
              optionFilterProp="label"
              onChange={handleWeatherChange}
              placeholder={t('list.weatherMapAuto')}
              notFoundContent={
                weatherMapsLoading || weatherMapsFetching
                  ? <Spin size="small" />
                  : weatherMapsError
                    ? t('list.weatherMapsLoadFailed')
                    : t('list.weatherMapBackendPicks')
              }
            />
          </Form.Item>
          {selectedWeatherMap ? (
            <Text type="secondary">
              {String(metadataOf(selectedWeatherMap).regionName || selectedWeatherMap.name)} · {String(metadataOf(selectedWeatherMap).koppen || t('list.climateFallback'))}
            </Text>
          ) : (
            <Text type="secondary">
              {t('list.weatherMapOptionalHint')}
            </Text>
          )}
          <Form.Item
            label={t('shared.measurementSystem')}
            name="measurementSystem"
            rules={[{ required: true, message: t('list.measurementRequired') }]}
            style={{ marginTop: 16 }}
          >
            <Segmented options={measurementOptions()} block />
          </Form.Item>
          <Text type="secondary">
            {t('list.measurementHint')}
          </Text>
          <Form.Item label={t('shared.startDate')} name="startDate" rules={[{ required: true, message: t('list.startDateRequired') }]} style={{ marginTop: 16 }}>
            <DatePicker style={{ width: '100%' }} format={datePickerFormat()} />
          </Form.Item>
          <Form.Item name="location_coords" hidden>
            <Input />
          </Form.Item>
          <Form.Item label={t('common:labels.description')} name="description">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <GuidedSaveCreationWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onCreated={(saveId) => navigate(`/saves/${saveId}`)}
      />
    </MainLayout>
  );
}
